import { Trans, useLingui } from '@lingui/react/macro';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { Identifiable } from '@/types/template';

interface SitesMultiSelectProps {
  sites: Identifiable[];
  value: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Site picker for the templates-landing filter bar (Task 6.9). A
 * compact combobox that collapses to a single trigger ("All sites" /
 * "N sites" native, "All sites" / "Site (N)" Rotaready) and reveals a
 * searchable check-list in a popover.
 *
 * Distinct from the generic `MultiSelect` primitive: optimised for the
 * filter-bar shape where the selected count matters more than the
 * individual chips, and where the popover should stay narrow rather
 * than grow with the selection.
 */
export function SitesMultiSelect({ sites, value, onChange }: SitesMultiSelectProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  // Cache the selected-ID set so per-row `selected` lookups and the
  // toggle helper both run in O(1) instead of O(n) per site per
  // render (matters once a tenant has hundreds of sites).
  const valueSet = useMemo(() => new Set(value), [value]);

  // The structural variant is driven by the `data-rrtb-theme` attribute
  // on the `.rrtb-root` host, so the closed cover switches without a JS
  // hook dependency on the module-level config registry.
  const isRotaready =
    (typeof document !== 'undefined' &&
      document.querySelector('.rrtb-root')?.getAttribute('data-rrtb-theme') === 'rotaready') ??
    false;

  const buttonText = isRotaready ? (
    value.length === 0 ? (
      <Trans>All sites</Trans>
    ) : (
      t`Site (${value.length})`
    )
  ) : value.length === 0 ? (
    <Trans>All sites</Trans>
  ) : value.length === 1 ? (
    <Trans>1 site</Trans>
  ) : (
    <Trans>{value.length} sites</Trans>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'min-w-40 justify-between',
            'rotaready:h-[30px] rotaready:min-w-0 rotaready:justify-start rotaready:rounded-[4px] rotaready:border-transparent rotaready:py-0 rotaready:text-base',
            'rotaready:hover:bg-[#475b68] rotaready:hover:text-white',
            value.length > 0
              ? 'rotaready:bg-[#475b68] rotaready:text-white'
              : 'rotaready:bg-transparent rotaready:text-[#475b68]',
          )}
        >
          {isRotaready && <MapPin aria-hidden="true" className="h-4 w-4" />}
          {buttonText}
          {!isRotaready && (
            <ChevronsUpDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={t`Search sites…`} />
          <CommandList>
            <CommandEmpty>
              <Trans>No sites found</Trans>
            </CommandEmpty>
            <CommandGroup>
              {sites.map((site) => {
                const selected = valueSet.has(site.id);
                return (
                  <CommandItem
                    key={site.id}
                    value={site.name}
                    onSelect={() => toggle(value, valueSet, site.id, onChange)}
                  >
                    <Check className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                    {site.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Toggles `id` in or out of the current selection and notifies the
 * parent via `onChange`. Uses the cached `currentSet` for the
 * membership check (O(1)); the resulting array is built from
 * `current` to preserve insertion order.
 */
function toggle(
  current: string[],
  currentSet: Set<string>,
  id: string,
  onChange: (next: string[]) => void,
) {
  onChange(currentSet.has(id) ? current.filter((x) => x !== id) : [...current, id]);
}
